import { useCallback, useRef } from 'react';
import { Canvas, FabricImage } from 'fabric';

type FitMode = 'contain' | 'cover';

/**
 * Hook for adding an image to the canvas via a hidden <input type="file">.
 *
 * Scales against the design dimensions so objects are placed in the same
 * coordinate space used by the rest of the editor (and by export).
 */
export function useImageUpload(
  canvas: Canvas | null,
  designWidth: number,
  designHeight: number,
  fitMode: FitMode = 'contain'
) {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!canvas) return;
      const file = event.target.files?.[0];
      if (!file) return;
      event.target.value = '';

      const url = URL.createObjectURL(file);
      const imgElement = new Image();
      imgElement.src = url;
      imgElement.onload = () => {
        const containScale = Math.min(designWidth / imgElement.width, designHeight / imgElement.height);
        const coverScale = Math.max(designWidth / imgElement.width, designHeight / imgElement.height);
        const scale = fitMode === 'cover' ? Math.min(coverScale, containScale * 1.5) : containScale;

        const fabricImage = new FabricImage(imgElement, {
          left: Math.round(designWidth / 2),
          top: Math.round(designHeight / 2),
          originX: 'center',
          originY: 'center',
          scaleX: scale,
          scaleY: scale,
          selectable: true
        });
        canvas.add(fabricImage);
        canvas.setActiveObject(fabricImage);
      };
    },
    [canvas, designWidth, designHeight, fitMode]
  );

  const open = useCallback(() => inputRef.current?.click(), []);

  return { open, inputRef, handleChange };
}
